// Notes Tool Entries for ToolRegistry

import { forwardCall, forwardCallByName } from 'workers-common'

export function createNoteTools({ getWorkerClient, notesPublicKey, getTarget }) {
  const callNotes = async (ctx, method, args) => {
    const workerClient = getWorkerClient()
    if (getTarget) {
      const target = await getTarget()
      return forwardCallByName(ctx, workerClient, target, `notes.${method}`, [args])
    }
    return forwardCall(ctx, workerClient, notesPublicKey, `notes.${method}`, [args])
  }

  return [
    {
      id: 'notes/saveNote',
      name: 'saveNote',
      source: 'builtin',
      description: 'Save information that user wants to remember (notes, facts, preferences, etc.). 保存用户想要记住的信息。',
      parameters: {
        type: 'object',
        properties: {
          content: { type: 'string', description: 'Note content (supports markdown)' },
          title: { type: 'string', description: 'Optional title (auto-generated if not provided)' },
          tags: { type: 'array', items: { type: 'string' }, description: 'Optional tags' },
        },
        required: ['content'],
      },
      searchText: 'saveNote\nSave note remember record memo write notes facts preferences\n保存笔记 新建笔记 记录 备忘 记住 写笔记 存一下',
      examples: [
        { input: 'save a note about the meeting', args: { content: 'Meeting notes from today', title: 'Meeting' } },
        { input: 'remember that the API key is xyz', args: { content: 'API key: xyz', tags: ['credentials'] } },
        { input: '帮我记一下明天开会', args: { content: '明天开会', title: '会议提醒' } },
      ],
      execute: (ctx, args) => callNotes(ctx, 'save', args),
    },
    {
      id: 'notes/searchNotes',
      name: 'searchNotes',
      source: 'builtin',
      description: 'Search user notes by semantic meaning. 搜索用户笔记。',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Search query' },
          limit: { type: 'number', description: 'Max results (default: 5)' },
        },
        required: ['query'],
      },
      searchText: 'searchNotes\nSearch find look up notes query\n搜索笔记 查找笔记 查询笔记 找笔记 找一下之前记的',
      examples: [
        { input: 'find my notes about meetings', args: { query: 'meetings' } },
        { input: 'what did I save about API keys', args: { query: 'API keys' } },
        { input: '找一下我之前记的会议笔记', args: { query: '会议' } },
      ],
      execute: (ctx, args) => callNotes(ctx, 'search', args),
    },
    {
      id: 'notes/listNotes',
      name: 'listNotes',
      source: 'builtin',
      description: 'List all notes for current user. 列出用户所有笔记。',
      parameters: {
        type: 'object',
        properties: {
          limit: { type: 'number', description: 'Max results (default: 10)' },
          offset: { type: 'number', description: 'Skip N notes (default: 0)' },
        },
      },
      searchText: 'listNotes\nList all notes show browse\n列出笔记 所有笔记 浏览笔记 看看我的笔记',
      examples: [
        { input: 'show all my notes', args: {} },
        { input: 'list my recent notes', args: { limit: 5 } },
        { input: '看看我所有的笔记', args: {} },
      ],
      execute: (ctx, args) => callNotes(ctx, 'list', args),
    },
    {
      id: 'notes/updateNote',
      name: 'updateNote',
      source: 'builtin',
      description: 'Update a note by its ID',
      parameters: {
        type: 'object',
        properties: {
          noteId: { type: 'string', description: 'Note ID' },
          content: { type: 'string', description: 'New content' },
          title: { type: 'string', description: 'New title' },
          tags: { type: 'array', items: { type: 'string' }, description: 'New tags' },
        },
        required: ['noteId'],
      },
      searchText: 'updateNote\nUpdate edit modify change note\n更新笔记 编辑笔记 修改笔记 改一下笔记',
      examples: [
        { input: 'update note abc123 with new content', args: { noteId: 'abc123', content: 'Updated content' } },
      ],
      execute: (ctx, args) => callNotes(ctx, 'update', args),
    },
    {
      id: 'notes/updateNoteByQuery',
      name: 'updateNoteByQuery',
      source: 'builtin',
      description: 'Update a note by searching for it first',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Search query to find the note' },
          newContent: { type: 'string', description: 'New content for the note' },
        },
        required: ['query', 'newContent'],
      },
      searchText: 'updateNoteByQuery\nUpdate edit note by searching find and modify\n搜索并更新笔记 查找并修改笔记 找到笔记改一下',
      examples: [
        { input: 'update my meeting notes with new info', args: { query: 'meeting notes', newContent: 'Updated meeting notes' } },
      ],
      execute: (ctx, args) => callNotes(ctx, 'updateByQuery', args),
    },
    {
      id: 'notes/deleteNote',
      name: 'deleteNote',
      source: 'builtin',
      description: 'Delete a note by its ID',
      parameters: {
        type: 'object',
        properties: {
          noteId: { type: 'string', description: 'Note ID' },
        },
        required: ['noteId'],
      },
      searchText: 'deleteNote\nDelete remove note\n删除笔记 移除笔记 去掉笔记',
      examples: [
        { input: 'delete note abc123', args: { noteId: 'abc123' } },
      ],
      execute: (ctx, args) => callNotes(ctx, 'delete', args),
    },
  ]
}
